import { Box, Button, Container, Divider, Grid, Typography } from '@material-ui/core';
import { lighten, Theme, withTheme } from '@material-ui/core/styles';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import ChildFriendlyIcon from '@material-ui/icons/ChildFriendly';
import ComputerIcon from '@material-ui/icons/Computer';
import EventIcon from '@material-ui/icons/Event';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';
import FlightIcon from '@material-ui/icons/Flight';
import { graphql } from 'gatsby';
import React from 'react';
import PageLayout from '../components/page-layout';
import Section from '../components/section';
import Seo from '../components/seo';

export const query = graphql`
  query jobPostingsQuery {
    allLever {
      edges {
        node {
          id
          lever_id
          createdAt
          text
          hostedUrl
          applyUrl
          description
          descriptionPlain
          categories {
            commitment
            location
            team
          }
          lists {
            text
            content
          }
          additional
        }
      }
    }
  }
`;

const benefits = [
  { icon: FlightIcon, label: 'Unlimited paid time off' },
  { icon: EventIcon, label: 'Three-day weekends every month' },
  { icon: ChildFriendlyIcon, label: 'Flexible parental leave' },
  { icon: ComputerIcon, label: '100% remote - anywhere in the US' },
  { icon: FavoriteBorderIcon, label: 'Inclusive and supportive working environment' },
  { icon: AttachMoneyIcon, label: 'Early stage equity' },
  { icon: AccountBalanceIcon, label: 'Equitable & objectively-determined compensation' },
];

const GradientIcon = props => (
  <>
    <svg width={0} height={0}>
      <linearGradient id="linearColors" gradientTransform="rotate(45)">
        <stop offset="5%" stopColor={props.startColor} />
        <stop offset="95%" stopColor={props.endColor} />
      </linearGradient>
    </svg>
    {React.createElement(props.icon, { style: { fill: 'url(#linearColors)', ...props.style } })}
  </>
);

type CareersPageProps = {
  theme: Theme;
  data: {
    allLever: {
      edges: Array<{
        node: {
          additional: string;
          applyUrl: string;
          createdAt: string;
          description: string;
          descriptionPlain: string;
          hostedUrl: string;
          id: string;
          lever_id: string;
          text: string;
          categories: {
            commitment: string;
            team: string;
            location: string;
          };
        };
      }>;
    };
  };
};

const CareersPage: React.FC<CareersPageProps> = props => {
  const rolesRef = React.createRef<HTMLDivElement>();
  const executeScroll = () => rolesRef.current.scrollIntoView({ behavior: 'smooth' });

  const benefitColor1 = lighten(props.theme.palette.info.light, 0.2);
  const benefitColor2 = lighten(props.theme.palette.primary.light, 0.2);

  return (
    <PageLayout>
      <Seo title="Careers" />

      <Section variant="hero-green">
        <Container maxWidth="md">
          <Typography variant="h1" align="center" paragraph>
            Join the team!
          </Typography>

          <Box textAlign="center" marginTop={4}>
            <Typography variant="subtitle1" align="center">
              We are an early, fast-growing company. That means we need people that love to grab a
              mission and figure out how to get it done. If that excites you, keep reading.
            </Typography>
          </Box>

          <Box textAlign="center" marginTop={4}>
            <Button
              onClick={executeScroll}
              color="primary"
              variant="contained"
              endIcon={<ArrowDownwardIcon />}
            >
              View Open Roles
            </Button>
          </Box>
        </Container>
      </Section>

      <Section>
        <Container maxWidth="lg">
          <Typography variant="h1" align="center" paragraph>
            Benefits
          </Typography>
          <Box textAlign="center" marginTop={4}>
            <Grid container justifyContent="center">
              {benefits.map((benefit, index) => {
                return (
                  <Grid sm={3} item key={index}>
                    <div style={{ padding: 20 }}>
                      <GradientIcon
                        icon={benefit.icon}
                        style={{ fontSize: 60 }}
                        startColor={benefitColor1}
                        endColor={benefitColor2}
                      />
                      <Typography variant="subtitle1">{benefit.label}</Typography>
                    </div>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Container>
      </Section>

      <Container>
        <Divider />
      </Container>

      <Section>
        <Container maxWidth="md">
          <div ref={rolesRef}></div>
          <Typography variant="h1" align="center" paragraph>
            Our open roles
          </Typography>

          <Box marginTop={4}>
            {props.data.allLever.edges.map(({ node }) => (
              <Typography key={node.id} style={{ fontSize: 30 }} align="left" component="div">
                <a target="_blank" href={node.hostedUrl}>
                  {node.text}
                </a>
                <Typography variant="caption" paragraph>
                  {node.categories.commitment}
                </Typography>
              </Typography>
            ))}
          </Box>
        </Container>
      </Section>
    </PageLayout>
  );
};

export default withTheme(CareersPage);
